import picomatch from "picomatch";
import * as React from "react";
import {
  NavigationGroupInterface,
  NavigationGroupWithChildrenInterface,
  NavigationItemInterface,
  NavigationPlaceInterface,
  useAppState,
  useOpenDashApp,
  usePath,
  usePermissions,
} from "..";

export function useNavigation(
  place: NavigationPlaceInterface
): [
  NavigationGroupWithChildrenInterface[],
  NavigationItemInterface[],
  string[],
] {
  const app = useOpenDashApp();
  const path = usePath();
  const permissions = usePermissions();

  const [userGroups, userItems] = useAppState((state) => [
    state.navigation.userNavigationGroups,
    state.navigation.userNavigationItems,
  ]);

  const groups = React.useMemo(() => {
    const x = [...userGroups, ...app.ui.staticNavigationGroups].sort(
      (a, b) => a.order - b.order
    );

    return x;
  }, [userGroups, path, place, permissions]);

  const items = React.useMemo(
    () =>
      [...userItems, ...app.ui.staticNavigationItems]
        .filter((item) => item.place === place)
        .filter(
          (item) =>
            !item.permission ||
            (Array.isArray(item.permission)
              ? item.permission.every((p) => permissions.includes(p))
              : permissions.includes(item.permission))
        )
        .filter((item) =>
          item.routeCondition
            ? picomatch.isMatch(path, item.routeCondition)
            : true
        )
        .sort((a, b) => a.order - b.order),
    [userItems, path, place, permissions]
  );

  const activeItems = React.useMemo(() => {
    return items
      .filter((item) =>
        item.activeCondition
          ? picomatch.isMatch(path, item.activeCondition)
          : false
      )
      .map((item) => item.id);
  }, [items, path]);

  const result: NavigationGroupWithChildrenInterface[] = React.useMemo(() => {
    return groups.map(mapGroup).filter((group) => {
      return group.children.length > 0 || group.hasItems;
    });
  }, [groups, items]);

  function mapGroup(group: NavigationGroupInterface) {
    const children = items.filter((item) => item.group === group.id);
    const subgroups = group.subgroups?.map(mapGroup);
    const hasItems = subgroups
      ? subgroups.some((subgroup) => subgroup.hasItems) || children.length > 0
      : children.length > 0;
    return {
      ...group,
      children: children,
      subgroups: subgroups,
      hasItems: hasItems,
    } as NavigationGroupWithChildrenInterface;
  }

  return [result, items, activeItems];
}
